import { useEffect, useRef, useState, useCallback } from 'react';

const ZOOM_MIN = 0.25;
const ZOOM_MAX = 3;

export interface UsePdfZoomOptions {
    scrollRef: React.RefObject<HTMLDivElement | null>;
    pdfLayersRef: React.RefObject<HTMLDivElement | null>;
    setActivePopup: React.Dispatch<React.SetStateAction<any>>;
}

export function usePdfZoom({ scrollRef, pdfLayersRef, setActivePopup }: UsePdfZoomOptions) {
    const [zoom, setZoom] = useState(1);
    const [zoomInput, setZoomInput] = useState('');
    const zoomAnchorRef = useRef<{ fx: number; fy: number; clientX: number; clientY: number } | null>(null);

    const anchorToCenter = useCallback(() => {
        const el = scrollRef.current;
        const content = pdfLayersRef.current;
        if (!el || !content) return;
        const er = el.getBoundingClientRect();
        const cx = er.left + er.width / 2;
        const cy = er.top + er.height / 2;
        const cr = content.getBoundingClientRect();
        zoomAnchorRef.current = {
            fx: Math.min(1, Math.max(0, (cx - cr.left) / cr.width)),
            fy: Math.min(1, Math.max(0, (cy - cr.top) / cr.height)),
            clientX: cx,
            clientY: cy,
        };
    }, [scrollRef, pdfLayersRef]);

    const zoomIn = useCallback(() => {
        setActivePopup(null);
        anchorToCenter();
        setZoom((z) => Math.min(ZOOM_MAX, +(z * 1.25).toFixed(3)));
    }, [anchorToCenter, setActivePopup]);

    const zoomOut = useCallback(() => {
        setActivePopup(null);
        anchorToCenter();
        setZoom((z) => Math.max(ZOOM_MIN, +(z * 0.8).toFixed(3)));
    }, [anchorToCenter, setActivePopup]);

    const zoomReset = useCallback(() => {
        setActivePopup(null);
        anchorToCenter();
        setZoom(1);
    }, [anchorToCenter, setActivePopup]);

    const applyZoomInput = useCallback(() => {
        const val = parseFloat(zoomInput.replace('%', '').trim());
        if (!isNaN(val) && val > 0) {
            anchorToCenter();
            setZoom(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, +(val / 100).toFixed(3))));
        }
        setZoomInput('');
    }, [zoomInput, anchorToCenter]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const onWheel = (e: WheelEvent) => {
            if (!e.ctrlKey) return;
            e.preventDefault();
            const content = pdfLayersRef.current;
            if (!content) return;
            const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
            if (!zoomAnchorRef.current) {
                const cr = content.getBoundingClientRect();
                zoomAnchorRef.current = {
                    fx: Math.min(1, Math.max(0, (e.clientX - cr.left) / cr.width)),
                    fy: Math.min(1, Math.max(0, (e.clientY - cr.top) / cr.height)),
                    clientX: e.clientX,
                    clientY: e.clientY,
                };
            }
            setActivePopup(null);
            setZoom((z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, +(z * factor).toFixed(3))));
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [scrollRef, pdfLayersRef, setActivePopup]);

    return {
        zoom,
        setZoom,
        zoomInput,
        setZoomInput,
        applyZoomInput,
        zoomIn,
        zoomOut,
        zoomReset,
        zoomAnchorRef,
    };
}
